import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

/**
 * Retrofits keyboard accessibility onto angular-6-datatable controls.
 *
 * The library renders sortable headers (mfDefaultSorter) and pagination links
 * (mfBootstrapPaginator) as `<a>` elements WITHOUT href, so they are skipped by
 * Tab / Shift+Tab and cannot be triggered from the keyboard. This directive
 * makes those controls focusable (tabindex="0", role="button") and activates
 * them on Enter / Space. It re-applies whenever the table re-renders (sorting
 * or paging swaps the DOM out).
 *
 * Apply to the `<table>` element that carries the datatable directives.
 */
@Directive({
  selector: '[appDatatableKeyboard]'
})
export class DatatableKeyboardDirective implements AfterViewInit, OnDestroy {

  private static readonly CONTROL_SELECTOR =
    'mfDefaultSorter > a, .pagination > .page-item:not(.disabled) > .page-link';

  private observer: MutationObserver;

  constructor(private readonly el: ElementRef<HTMLElement>, private readonly zone: NgZone) {
  }

  ngAfterViewInit(): void {
    // The observer only tweaks DOM attributes, so keep it out of Angular's zone
    // to avoid triggering change detection on every mutation.
    this.zone.runOutsideAngular(() => {
      this.makeControlsFocusable();
      this.observer = new MutationObserver(() => this.makeControlsFocusable());
      this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
    });
    this.el.nativeElement.addEventListener('keydown', this.onKeydown);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.el.nativeElement.removeEventListener('keydown', this.onKeydown);
  }

  private makeControlsFocusable(): void {
    const controls = this.el.nativeElement.querySelectorAll<HTMLElement>(DatatableKeyboardDirective.CONTROL_SELECTOR);
    Array.from(controls).forEach(control => {
      if (!control.hasAttribute('tabindex')) {
        control.setAttribute('tabindex', '0');
      }
      if (!control.hasAttribute('role')) {
        control.setAttribute('role', 'button');
      }
    });
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    const isEnter = event.key === 'Enter' || event.keyCode === 13;
    const isSpace = event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32;
    if (!isEnter && !isSpace) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target && target.matches(DatatableKeyboardDirective.CONTROL_SELECTOR)) {
      // A real click bubbles from the <a> to the (click) handler the library
      // put on the element (sorter) or its parent <li> (paginator).
      event.preventDefault();
      target.click();
    }
  };

}